import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  Typography,
  styled,
} from "@mui/material";
import { FieldArray, Form, Formik, FormikHelpers } from "formik";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { ReactElement, ReactNode, useMemo, useState } from "react";
import * as Yup from "yup";

import validateMessageScript, {
  messagingStrategyRequiresOptOutLanguage,
} from "@stw/message-script-validator";
import { DEFAULT_AUTO_MMS_BREAKPOINT } from "@stw/sms-text-encoding";

import {
  CampaignStatus,
  CreateCampaignPersonalizedGifMutation,
  CreateCampaignPersonalizedGifMutationVariables,
  EditCampaignScriptVariantsMutation,
  EditCampaignScriptVariantsMutationVariables,
  PersonalizedGifStatus,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import Loading from "~frontend/components/Loading";
import RouteLeavingGuard from "~frontend/components/RouteLeavingGuard";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import {
  MediaFieldValue,
  UploadFileFields,
  mediaValidationSchema,
} from "~frontend/components/forms/FormikMediaField";
import { VALID_PERSONALIZED_GIF_MESSAGING_STRATEGIES } from "~frontend/components/personalizedGifs/types";
import {
  CREATE_CAMPAIGN_PERSONALIZED_GIF,
  EDIT_CAMPAIGN_SCRIPT_VARIANTS,
  GET_CAMPAIGN_FOR_VIEW,
} from "~frontend/graphql/campaigns";
import { useMediaUpload } from "~frontend/hooks/useMediaUpload";
import { useTenant } from "~frontend/providers/LoggedInUserProvider";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccountAdminConfig } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import { fetchOptOutPhraseList } from "~frontend/util/optOutPhrases";
import useMutation from "~frontend/util/useMutation";

import { CampaignPreviewMessagesTable } from "../campaignView/CampaignPreviewMessagesTable";
import { PersonalizedGifFieldValue } from "./Script/ScriptEditor";
import { ScriptVariants } from "./Script/ScriptVariants";
import { ScriptVariantInput } from "./Script/types";
import useOptOutPhraseList from "./Script/useOptOutPhraseList";
import { StepInstructions } from "./StepInstructions";
import { FormikSaveButtons } from "./campaignEditorUtils/SaveButtons";
import { CampaignForEditor } from "./types";

// The script editor and preview has a much tighter layout than the default MUI
// components. This requires overriding paddings and margins.
export const ScriptContainer = styled("div")({
  "& input": {
    fontSize: "0.875rem",
  },
  "& .MuiInputBase-root": {
    marginBottom: "0 !important",
  },
  "& .MuiTextField-root": {
    marginBottom: 0,
  },
});

export type CampaignEditorValues = {
  [submitErrorField]?: string | null;
  variants: {
    [submitErrorField]?: string | null;
    id?: string;
    displayOrder?: number | null;
    script: string;
    media: MediaFieldValue;
    overrideLinkShortenerSettings: boolean;
    linkShortenerDomainName: string;
    personalizedGif: PersonalizedGifFieldValue;
  }[];
  goToNext?: boolean;
};

export const InitialMessage = ({
  campaign,
  goToNextSection,
}: {
  campaign: CampaignForEditor;
  goToNextSection: (opts?: { bypassRouteLeavingGuard?: boolean }) => void;
}): ReactElement => {
  const getErrorMessage = useErrorMessageFormatter();
  const { enqueueSnackbar } = useSnackbar();
  const tenant = useTenant()!;
  const subaccount = useSubaccountAdminConfig()!;

  const { phraseList: optOutPhrases, loading: optOutPhrasesLoading } =
    useOptOutPhraseList();

  const { uploadMedia } = useMediaUpload();

  const [createCampaignPersonalizedGif] = useMutation<
    CreateCampaignPersonalizedGifMutation,
    CreateCampaignPersonalizedGifMutationVariables
  >(CREATE_CAMPAIGN_PERSONALIZED_GIF, {
    onErrorSnackbarMessage: "Error creating personalized gif",
  });

  const [editCampaignScriptVariants] = useMutation<
    EditCampaignScriptVariantsMutation,
    EditCampaignScriptVariantsMutationVariables
  >(EDIT_CAMPAIGN_SCRIPT_VARIANTS, {
    onErrorSnackbarMessage: "Could not edit campaign",
    refetchQueries: ["getCampaign", GET_CAMPAIGN_FOR_VIEW],
    awaitRefetchQueries: true,
  });

  const [previewHistoryOpen, setPreviewHistoryOpen] = useState(false);

  const handleSave = async (
    { variants, goToNext }: CampaignEditorValues,
    {
      setSubmitting,
      resetForm,
      setErrors,
      setFieldError,
    }: FormikHelpers<CampaignEditorValues>
  ) => {
    const variantsWithMediaOrPersonalizedGifs: (ScriptVariantInput & {
      mediaUploadId?: string | null;
      personalizedGifId?: string | null;
    })[] = await Promise.all(
      variants.map(async variant => {
        if (variant.media && (variant.media.upload || variant.media.file)) {
          const mediaUpload = await uploadMedia(variant.media);
          return { ...variant, mediaUploadId: mediaUpload?.id };
        }

        if (variant.personalizedGif) {
          if (variant.personalizedGif.personalizedGif) {
            const createResponse = await createCampaignPersonalizedGif({
              variables: {
                input: {
                  campaignId: campaign.id,
                  name: variant.personalizedGif.personalizedGif.name,
                  creatomateTemplateId:
                    variant.personalizedGif.personalizedGif.creatomateTemplate
                      .id,
                  customFields:
                    variant.personalizedGif.personalizedGif.customFields.map(
                      customField => _.omit(customField, "__typename")
                    ),
                  customColors:
                    variant.personalizedGif.personalizedGif.customColors.map(
                      customColor => _.omit(customColor, "__typename")
                    ),
                  customLogo: variant.personalizedGif.personalizedGif.customLogo
                    ? _.omit(
                        variant.personalizedGif.personalizedGif.customLogo,
                        "__typename"
                      )
                    : null,
                  contactFieldMetadata: variant.personalizedGif.personalizedGif
                    .contactFieldMetadata
                    ? variant.personalizedGif.personalizedGif.contactFieldMetadata.map(
                        md => _.omit(md, "__typename")
                      )
                    : null,
                  previewSnapshotTimeSeconds:
                    variant.personalizedGif.personalizedGif
                      .previewSnapshotTimeSeconds,
                },
              },
            });
            return {
              ...variant,
              personalizedGifId:
                createResponse.data!.createCampaignPersonalizedGif.id,
            };
          } else if (variant.personalizedGif.campaignPersonalizedGif) {
            return {
              ...variant,
              personalizedGifId:
                variant.personalizedGif.campaignPersonalizedGif.id,
            };
          }
        }

        return variant;
      })
    );

    const { errors, data } = await editCampaignScriptVariants({
      variables: {
        input: {
          campaignId: campaign.id,
          variants: variantsWithMediaOrPersonalizedGifs.map(
            ({
              id,
              script,
              mediaUploadId,
              mediaContactFieldName,
              overrideLinkShortenerSettings,
              linkShortenerDomainName,
              personalizedGifId,
            }) => ({
              id,
              script,
              mediaUploadId,
              mediaContactFieldName: mediaContactFieldName
                ? mediaContactFieldName
                : null,
              overrideLinkShortenerSettings,
              linkShortenerDomainName: linkShortenerDomainName
                ? linkShortenerDomainName
                : null,
              personalizedGifId,
            })
          ),
        },
      },
    });

    setSubmitting(false);

    const updatedVariants = data?.editCampaignScriptVariants ?? [];
    let hasError = false;

    updatedVariants.forEach((variant, index) => {
      if (!variant.ok && variant.error) {
        setFieldError(`variants.${index}.${submitErrorField}`, variant.error);
        hasError = true;
      }
    });

    if (errors) {
      setErrors({ [submitErrorField]: getErrorMessage(errors) });
    } else if (!hasError) {
      resetForm({
        values: {
          variants: updatedVariants.map(variant => ({
            id: variant.id ?? undefined,
            displayOrder: variant.displayOrder,
            script: variant.script,
            media: variant.mediaUpload ? { upload: variant.mediaUpload } : null,
            mediaContactFieldName: variant.mediaContactFieldName || "",
            overrideLinkShortenerSettings:
              variant.overrideLinkShortenerSettings,
            linkShortenerDomainName: variant.linkShortenerDomainName || "",
            personalizedGif: variant.personalizedGif
              ? {
                  campaignPersonalizedGif: variant.personalizedGif,
                  personalizedGif: null,
                }
              : null,
          })),
        },
      });

      if (goToNext) {
        goToNextSection({ bypassRouteLeavingGuard: true });
      } else {
        enqueueSnackbar("Saved campaign", { variant: "info" });
      }
    }
  };

  const roiTestInProgress = useMemo(() => {
    if (campaign.status === CampaignStatus.InitialSendRunning) {
      return !!campaign.roiTestConfig;
    } else if (campaign.status === CampaignStatus.RoiTestAutosendPending) {
      return true;
    }
    return false;
  }, [campaign]);

  const personalizedGifsEnabled = useFeatureFlag("personalizedGifs");

  const hasValidMessagingStrategyForPersonalizedGifs =
    campaign && campaign.messagingStrategy
      ? VALID_PERSONALIZED_GIF_MESSAGING_STRATEGIES.includes(
          campaign.messagingStrategy.strategy
        )
      : false;

  if (optOutPhrasesLoading) {
    return <Loading />;
  }

  return (
    <ScriptContainer>
      <Box sx={{ pb: 4 }}>
        <StepInstructions header="Initial Message">
          <Box>
            Write your initial message to send to your contacts here. You can
            use the name of any of your mapped contact fields surrounded by
            brackets to customize your message (example: {"{{first_name}}"}
            ).
          </Box>
          <Box>
            Your initial message must include opt out language, such as
            &ldquo;Stop to Opt Out.&rdquo;
          </Box>
          <Box>
            Media files can be a picture, video, GIF, or contact card. Files
            must be jpg, png, gif, mp4, or vcard format and under 500 KB.
          </Box>
          <Box>
            Friendly reminder: We charge per segment for SMS and per message for
            MMS. If you send a message with{" "}
            {tenant.autoMMSBreakpoint || DEFAULT_AUTO_MMS_BREAKPOINT} or more
            segments, it will be billed as an MMS message to cap your messaging
            costs. The segment estimates shown below are based on the contact
            field values you provide; actual segment counts will vary for each
            contact based on the character count of the actual text sent once
            contact field values are populated.
          </Box>
          {personalizedGifsEnabled &&
            !hasValidMessagingStrategyForPersonalizedGifs && (
              <Box>
                Note: Personalized GIFs are only supported on Bandwidth
                campaigns. If you plan to add a personalized GIF, please update
                your phone number strategy on the Sending Numbers step.
              </Box>
            )}
          {personalizedGifsEnabled &&
            hasValidMessagingStrategyForPersonalizedGifs && (
              <Box>
                Note: you can only include two personalized gif templates per
                campaign.
              </Box>
            )}
        </StepInstructions>
        {personalizedGifsEnabled &&
          campaign.personalizedGifStatus ===
            PersonalizedGifStatus.Generated && (
            <Box sx={{ pt: "16px" }}>
              <Alert severity="warning">
                Personalized GIFs can no longer be edited because the GIFs for
                this campaign have already been generated.
              </Alert>
            </Box>
          )}
        {personalizedGifsEnabled &&
          campaign.personalizedGifStatus === PersonalizedGifStatus.None && (
            <Box sx={{ pt: "16px" }}>
              <Alert severity="warning">
                Personalized GIFs can no longer be edited because this campaign
                has already started sending.
              </Alert>
            </Box>
          )}
      </Box>

      <Formik
        initialValues={
          {
            [submitErrorField]: null,
            variants: campaign.scriptVariants.map(variant => ({
              [submitErrorField]: null,
              id: variant.id,
              displayOrder: variant.displayOrder,
              script: variant.script,
              media: { upload: variant.mediaUpload as UploadFileFields },
              mediaContactFieldName: variant.mediaContactFieldName || "",
              overrideLinkShortenerSettings:
                variant.overrideLinkShortenerSettings,
              linkShortenerDomainName: variant.linkShortenerDomainName || "",
              personalizedGif: variant.personalizedGif
                ? { campaignPersonalizedGif: variant.personalizedGif }
                : null,
            })),
          } as CampaignEditorValues
        }
        validationSchema={Yup.object({
          variants: Yup.array()
            .of(
              Yup.object({
                script: Yup.string()
                  .required("Script text is required")
                  .test({
                    name: "script-test",
                    test: function (value, context) {
                      if (!value) {
                        return true;
                      }

                      const parent =
                        context.parent as CampaignEditorValues["variants"][0];
                      const isUsingLinkShortener =
                        !!(parent.overrideLinkShortenerSettings
                          ? parent.linkShortenerDomainName
                          : subaccount.defaultLinkShortenerDomainName);

                      const validationResult = validateMessageScript(value, {
                        validVariables: new Set(
                          campaign.contactFields.map(field => field.name)
                        ),
                        requireOptOutLanguage:
                          messagingStrategyRequiresOptOutLanguage(
                            campaign.messagingStrategy.strategy
                          ),
                        optOutPhrases,
                        validateLinks: !isUsingLinkShortener,
                      });
                      if (!validationResult.ok) {
                        return this.createError({
                          message: validationResult.error,
                          path: context.path,
                        });
                      }

                      return true;
                    },
                  }),
                media: mediaValidationSchema(),
                mediaContactFieldName: Yup.string(),
                overrideLinkShortenerSettings: Yup.boolean(),
                linkShortenerDomainName: Yup.string(),
              })
            )
            .required("Required"),
        })}
        onSubmit={handleSave}
        validateOnChange={true}
      >
        {({ dirty, values, errors, touched }) => {
          const hasValidationErrors =
            !!errors.variants &&
            (typeof errors.variants === "string" ||
              errors.variants.some(
                (error, index) =>
                  typeof error === "string" ||
                  (touched.variants?.[index]?.script && !!error?.script) ||
                  (touched.variants?.[index]?.media && !!error?.media)
              ));

          const hasErrors = !!(errors[submitErrorField] || hasValidationErrors);

          return (
            <Form>
              <RouteLeavingGuard when={dirty} />
              <FormikErrorBanner noScrollOnError />
              <FieldArray name="variants">
                {({ remove, push }) => (
                  <>
                    <FlexBox direction="column" gap={2} sx={{ mb: 3 }}>
                      {roiTestInProgress && (
                        <Alert severity="warning" icon={false}>
                          <Typography variant="body2Bold">Note:</Typography>{" "}
                          There is currently an ROI test running on these script
                          variants. Any edits made to these scripts may impact
                          the results of the test.
                        </Alert>
                      )}

                      <ScriptVariants
                        campaign={campaign}
                        values={values.variants}
                        errors={errors}
                        touched={touched}
                        onDelete={(index: number) => remove(index)}
                        revealSendStatus={() => setPreviewHistoryOpen(true)}
                      />

                      {hasErrors && (
                        <FlexBox justifyContent="flex-end">
                          <Typography sx={{ color: palette.red, pt: "4px" }}>
                            You must resolve the issues with your initial
                            messages before saving.
                          </Typography>
                        </FlexBox>
                      )}
                    </FlexBox>

                    <FlexBox justifyContent="space-between" sx={{ pt: 1 }}>
                      <FlexBox sx={{ gap: 2 }}>
                        <Button
                          variant="contained"
                          color="tertiary"
                          onClick={() =>
                            push({
                              script: "",
                              media: {},
                              overrideLinkShortenerSettings: false,
                              linkShortenerDomainName: "",
                            })
                          }
                        >
                          + Add a Variant
                        </Button>

                        {campaign.hasPreviewMessages && (
                          <Button
                            variant="contained"
                            color="tertiary"
                            onClick={() => setPreviewHistoryOpen(true)}
                          >
                            View Preview History
                          </Button>
                        )}
                      </FlexBox>

                      <FormikSaveButtons
                        disabled={hasValidationErrors}
                        continueWithoutSaving={goToNextSection}
                      />
                    </FlexBox>
                  </>
                )}
              </FieldArray>
            </Form>
          );
        }}
      </Formik>

      {previewHistoryOpen && (
        <Dialog
          open={true}
          onClose={() => setPreviewHistoryOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <CampaignPreviewMessagesTable
            campaignShortId={campaign.shortId}
            alwaysShowVariant={campaign.scriptVariants.length > 1}
            dialogLayout
          />
          <DialogActions>
            <Button
              onClick={() => setPreviewHistoryOpen(false)}
              color="tertiary"
              variant="contained"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </ScriptContainer>
  );
};

export async function validateScripts({
  campaign,
}: {
  campaign: CampaignForEditor;
}): Promise<{ title: string; message: ReactNode }[]> {
  const hasEmptyVariants =
    !campaign.scriptVariants ||
    campaign.scriptVariants.length === 0 ||
    campaign.scriptVariants.some(
      variant => !variant.script || variant.script?.length === 0
    );

  if (hasEmptyVariants) {
    return [
      {
        title: "No Initial Message",
        message: "You must write an initial message to start your campaign.",
      },
    ];
  }

  const requireOptOutLanguage = messagingStrategyRequiresOptOutLanguage(
    campaign.messagingStrategy.strategy
  );

  const optOutPhrases = requireOptOutLanguage
    ? await fetchOptOutPhraseList()
    : [];

  const validationResults = campaign.scriptVariants.map(variant =>
    validateMessageScript(variant.script, {
      validVariables: false,
      validateLinks: false,
      requireOptOutLanguage,
      optOutPhrases,
    })
  );

  return validationResults
    .filter(res => !res.ok)
    .map(res => ({ title: "Error in Initial Message", message: res.error }));

  return [];
}
